/**
 * Generic Legal Document PDF Renderer
 *
 * Renders any LegalDocument to PDF format using @react-pdf/renderer.
 * This component works with the block-based document schema and supports
 * any template type (employment agreements, NDAs, etc.).
 */

import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';

import { LegalDocument } from '@/schemas/schema';
import { BlockRenderer } from './components/BlockRenderer';
import { SignaturePage } from './components/SignaturePage';

// ==========================================
// STYLES
// ==========================================

/**
 * Professional Legal Document Styles
 * DocuSign-inspired clean design with proper legal formatting
 */
const styles = StyleSheet.create({
  page: {
    // `size` belongs on the <Page> prop, NOT in styles — leaving it here
    // forces LETTER and silently overrides the dynamic `effectivePageSize`
    // we build below for letterhead-aware sizing, which would clip the
    // letterhead's bottom on A4 letterheads. Page-size is now driven by
    // the prop only.
    padding: 72, // 1 inch margins
    fontFamily: 'Helvetica',
    fontSize: 10.5,
    lineHeight: 1.6,
    backgroundColor: '#ffffff',
  },

  // Header
  header: {
    marginBottom: 32,
    paddingBottom: 12,
    borderBottom: '0.5pt solid #e5e7eb',
  },
  title: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#111827',
  },

  // Document Body
  effectiveDate: {
    fontSize: 11,
    fontFamily: 'Helvetica',
    marginBottom: 12,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 36,
    left: 72,
    right: 72,
    borderTop: '0.5pt solid #e5e7eb',
    paddingTop: 8,
    fontSize: 7.5,
    color: '#9ca3af',
  },
  footerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

/**
 * Format date string for display in document
 * Uses locale from document metadata for jurisdiction-appropriate formatting
 * @param dateString - The date string to format
 * @param locale - The locale to use (e.g., 'en-US', 'en-GB', 'de-DE')
 */
const formatDate = (dateString: string, locale = 'en-US'): string => {
  const parsedDate = new Date(dateString);

  if (Number.isNaN(parsedDate.getTime())) {
    return dateString;
  }

  return parsedDate.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format page number string using template from document metadata
 * @param pageNumber - Current page number
 * @param totalPages - Total number of pages
 * @param format - Page number format template (default: "Page {page} of {total}")
 */
const formatPageNumber = (
  pageNumber: number,
  totalPages: number,
  format = 'Page {page} of {total}'
): string => {
  return format.replace('{page}', String(pageNumber)).replace('{total}', String(totalPages));
};

// ==========================================
// COMPONENT
// ==========================================

export interface LegalDocumentPDFProps {
  /** The legal document to render */
  document: LegalDocument;
  /** Optional document ID for tracking */
  docId?: string;
  /** Whether to include signature page (default: true) */
  includeSignaturePage?: boolean;
  /** Custom page size (default: 'LETTER') */
  pageSize?: 'LETTER' | 'A4' | 'LEGAL';
}

/**
 * Generic Legal Document PDF Component
 *
 * Renders a LegalDocument to PDF format with:
 * - Professional header with document title
 * - Effective date
 * - Block-based content rendering
 * - Signature page (optional)
 * - Page numbers in footer
 */
export const LegalDocumentPDF: React.FC<LegalDocumentPDFProps> = ({
  document,
  // docId,
  includeSignaturePage = true,
  pageSize = 'LETTER',
}) => {
  // Validate document has required fields
  if (!document?.metadata?.title || !document?.content) {
    return (
      <Document>
        <Page size={pageSize} style={styles.page}>
          <Text>Invalid document format. Please regenerate the document.</Text>
        </Page>
      </Document>
    );
  }

  // Check if we should include signatures in the document flow
  const hasSignatories =
    includeSignaturePage && document.signatories && document.signatories.length > 0;

  // Get letterhead configuration
  const letterhead = document.letterhead;
  const hasLetterhead = !!letterhead;

  // Default 1-inch margin in points (72 points = 1 inch)
  // const DEFAULT_MARGIN = 72;

  // Calculate content area margins from letterhead (only when letterhead exists)
  // For subsequent pages, we use the same left/right margins but reset top/bottom to defaults
  // const contentMargins = hasLetterhead
  //   ? {
  //       paddingTop: letterhead.contentArea.y,
  //       paddingLeft: letterhead.contentArea.x,
  //       paddingRight:
  //         letterhead.pageWidth - letterhead.contentArea.x - letterhead.contentArea.width,
  //       paddingBottom:
  //         letterhead.pageHeight - letterhead.contentArea.y - letterhead.contentArea.height,
  //     }
  //   : undefined;

  // Margins for continuation pages (2nd page and beyond) - use left/right from letterhead but standard top/bottom
  // const continuationMargins = hasLetterhead
  //   ? {
  //       paddingTop: DEFAULT_MARGIN,
  //       paddingLeft: letterhead.contentArea.x,
  //       paddingRight:
  //         letterhead.pageWidth - letterhead.contentArea.x - letterhead.contentArea.width,
  //       paddingBottom: DEFAULT_MARGIN,
  //     }
  //   : undefined;

  // Create dynamic page style with letterhead support
  // Apply content area as page padding so text stays within bounds on ALL pages
  const pageStyle = hasLetterhead
    ? {
        ...styles.page,
        // Use content area to define page margins
        paddingTop: letterhead.contentArea.y,
        paddingLeft: letterhead.contentArea.x,
        paddingRight:
          letterhead.pageWidth - letterhead.contentArea.x - letterhead.contentArea.width,
        paddingBottom:
          letterhead.pageHeight - letterhead.contentArea.y - letterhead.contentArea.height,
        backgroundColor: '#ffffff',
      }
    : styles.page;

  // When a letterhead is present the page must match the letterhead's
  // dimensions exactly. The Image fills the page using letterhead.pageWidth
  // ×pageHeight; if the page is a different size (e.g. hardcoded LETTER
  // 612×792 when the letterhead is A4 595×842), the image overflows and
  // its bottom/right edge gets clipped. The padding math also uses the
  // same dimensions, so a mismatched page misaligns the content area.
  const effectivePageSize: [number, number] | typeof pageSize = hasLetterhead
    ? [letterhead.pageWidth, letterhead.pageHeight]
    : pageSize;

  return (
    <Document>
      {/* Single Document with content and signatures flowing together */}
      <Page size={effectivePageSize} style={pageStyle} wrap>
        {/* Letterhead Background - MUST be first to render behind content */}
        {hasLetterhead && (
          <Image
            src={letterhead.imageDataUrl}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: letterhead.pageWidth,
              height: letterhead.pageHeight,
            }}
            fixed
          />
        )}

        {/* Content - Page padding from letterhead.contentArea ensures text stays within bounds on ALL pages */}
        {hasLetterhead ? (
          <>
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.title}>{document.metadata.title}</Text>
            </View>

            {/* Effective Date */}
            {document.metadata.effectiveDate && (
              <View style={{ marginBottom: 12 }}>
                <Text style={styles.effectiveDate}>
                  {document.metadata.effectiveDateLabel || 'Effective Date:'}{' '}
                  {formatDate(document.metadata.effectiveDate, document.metadata.dateLocale)}
                </Text>
              </View>
            )}

            {/* Main Content Blocks */}
            {document.content.map((block, index) => (
              <BlockRenderer key={block.id || index} block={block} level={0} />
            ))}

            {/* Signature Section - flows naturally after content, breaks to new page if needed */}
            {/* minPresenceAhead ensures the entire signature section stays together */}
            {hasSignatories && document.signatories && (
              <View break style={{ marginTop: hasLetterhead ? 0 : 40 }}>
                <SignaturePage
                  signatories={document.signatories}
                  config={document.signaturePageConfig}
                />
              </View>
            )}
          </>
        ) : (
          <>
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.title}>{document.metadata.title}</Text>
            </View>

            {/* Effective Date */}
            {document.metadata.effectiveDate && (
              <View style={{ marginBottom: 12 }}>
                <Text style={styles.effectiveDate}>
                  {document.metadata.effectiveDateLabel || 'Effective Date:'}{' '}
                  {formatDate(document.metadata.effectiveDate, document.metadata.dateLocale)}
                </Text>
              </View>
            )}

            {/* Main Content Blocks */}
            {document.content.map((block, index) => (
              <BlockRenderer key={block.id || index} block={block} level={0} />
            ))}

            {/* Signature Section - flows naturally after content, breaks to new page if needed */}
            {hasSignatories && document.signatories && (
              <View break style={{ marginTop: 40 }}>
                <SignaturePage
                  signatories={document.signatories}
                  config={document.signaturePageConfig}
                />
              </View>
            )}
          </>
        )}

        {/* Footer on all pages */}
        {!hasLetterhead && (
          <View style={styles.footer} fixed>
            <View style={styles.footerContent}>
              <Text>{document.metadata.title}</Text>
              <Text
                render={({ pageNumber, totalPages }) =>
                  formatPageNumber(pageNumber, totalPages, document.metadata.pageNumberFormat)
                }
              />
            </View>
          </View>
        )}
      </Page>
    </Document>
  );
};

export default LegalDocumentPDF;
